import { useState, type FormEvent } from 'react';
import { Trade, VendorInput, type Vendor } from '@syndiqia/maintenance/contracts';
import { errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useMaintenanceMutation, useVendors } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { MaintenanceFrame, todayIso } from './MaintenanceNav';

export function VendorsPage() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const vendors = useVendors();
  const [editing, setEditing] = useState<string | null>(null);
  const manages = s.can('maintenance.manage');
  const today = todayIso();
  const soon = new Date(Date.parse(today) + 30 * 86_400_000).toISOString().slice(0, 10);

  return (
    <MaintenanceFrame intro={m.vendorsIntro} actions={manages && <Disclosure label={m.addVendor}>{(close) => <VendorForm onDone={close} />}</Disclosure>}>
      {vendors.isPending && <Loading label={t.loading} />}
      {vendors.isError && <Alert>{errorMessage(vendors.error, t)}</Alert>}
      {vendors.data?.length === 0 && <Empty>{m.noVendors}</Empty>}
      {!!vendors.data?.length && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{m.vendorName}</th>
              <th>{m.trade}</th>
              <th>{m.contactName}</th>
              <th>{m.rbq}</th>
              <th>{m.insuranceExpires}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {vendors.data.map((v) =>
              editing === v.id ? (
                <tr key={v.id}>
                  <td colSpan={6}>
                    <VendorForm vendor={v} onDone={() => setEditing(null)} />
                  </td>
                </tr>
              ) : (
                <tr key={v.id} className={v.active ? undefined : 'row-muted'}>
                  <td className="cell-title">
                    {v.name}
                    {!v.active && <span className="muted small"> · {m.inactive}</span>}
                  </td>
                  <td data-label={m.trade}>{m.trades[v.trade]}</td>
                  <td data-label={m.contactName}>
                    {v.contactName && <div>{v.contactName}</div>}
                    {v.email && (
                      <div className="small">
                        <a href={`mailto:${v.email}`}>{v.email}</a>
                      </div>
                    )}
                    {v.phone && <div className="small">{v.phone}</div>}
                    {!v.contactName && !v.email && !v.phone && m.none}
                  </td>
                  <td data-label={m.rbq}>{v.rbqLicence ?? m.none}</td>
                  <td data-label={m.insuranceExpires}>
                    {v.insuranceExpiresOn ? date(v.insuranceExpiresOn) : m.none}
                    {v.active && v.insuranceExpiresOn && v.insuranceExpiresOn < today && <span className="badge badge-urgent">{m.insuranceExpired}</span>}
                    {v.active && v.insuranceExpiresOn && v.insuranceExpiresOn >= today && v.insuranceExpiresOn < soon && (
                      <span className="badge badge-status-waiting">{m.insuranceSoon}</span>
                    )}
                  </td>
                  <td className="cell-actions">
                    {manages && (
                      <button type="button" className="link-button small" onClick={() => setEditing(v.id)}>
                        {m.edit}
                      </button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </MaintenanceFrame>
  );
}

function VendorForm({ vendor, onDone }: { vendor?: Vendor; onDone: () => void }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const [values, setValues] = useState({
    name: vendor?.name ?? '',
    trade: vendor?.trade ?? 'general',
    contactName: vendor?.contactName ?? '',
    email: vendor?.email ?? '',
    phone: vendor?.phone ?? '',
    rbqLicence: vendor?.rbqLicence ?? '',
    insuranceExpiresOn: vendor?.insuranceExpiresOn ?? '',
    notes: vendor?.notes ?? '',
  });
  const [active, setActive] = useState(vendor?.active ?? true);
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMaintenanceMutation<VendorInput, Vendor>((api, base, input) =>
    api(vendor ? `${base}/vendors/${vendor.id}` : `${base}/vendors`, { method: vendor ? 'PUT' : 'POST', body: input }),
  );
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(VendorInput, { ...values, active });
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={m.vendorName} error={errors.name}>
          <input value={values.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={m.trade}>
          <select value={values.trade} onChange={set('trade')}>
            {Trade.options.map((x) => (
              <option key={x} value={x}>
                {m.trades[x]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label={m.contactName} hint={t.common.optional}>
          <input value={values.contactName} onChange={set('contactName')} />
        </Field>
        <Field label={m.email} hint={t.common.optional} error={errors.email}>
          <input type="email" value={values.email} onChange={set('email')} />
        </Field>
        <Field label={m.phone} hint={t.common.optional}>
          <input type="tel" value={values.phone} onChange={set('phone')} />
        </Field>
      </div>
      <div className="row">
        <Field label={m.rbq} hint={t.common.optional}>
          <input value={values.rbqLicence} onChange={set('rbqLicence')} placeholder="0000-0000-00" />
        </Field>
        <Field label={m.insuranceExpires} hint={t.common.optional} error={errors.insuranceExpiresOn}>
          <input type="date" value={values.insuranceExpiresOn} onChange={set('insuranceExpiresOn')} />
        </Field>
      </div>
      <Field label={m.notes} hint={t.common.optional}>
        <textarea rows={2} value={values.notes} onChange={set('notes')} />
      </Field>
      {vendor && (
        <label className="inline-field">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          {m.active}
        </label>
      )}
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
